import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { getAuthToken } from '../../auth/context/AuthContext';
import PasswordField from '../../../shared/components/PasswordField';
import { passwordError } from '../../../shared/utils/passwordPolicy';

const API = `${process.env.REACT_APP_API_URL || 'https://recruitai.pingitright.com/api'}/auth`;

export default function ChangePassword() {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [err, setErr] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const savePassword = async (e) => {
    e.preventDefault();
    setErr('');
    setNote('');
    const problem = passwordError(newPassword);
    if (problem) {
      setErr(problem);
      return;
    }
    if (newPassword !== confirmPassword) {
      setErr('New password and confirmation do not match');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API}/change-password`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof data.detail === 'string' ? data.detail : 'Could not change password');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setNote('Password changed.');
    } catch (error) {
      setErr(error.message);
    }
    setSaving(false);
  };

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <div className="page-title">Change password</div>
          <div className="page-subtitle">Enter your current password, then choose a new one.</div>
        </div>
      </div>
      <form className="card" onSubmit={savePassword} style={{ maxWidth: 520 }}>
        {err && <div className="alert alert-error" style={{ marginBottom: 12 }}>{err}</div>}
        {note && <div style={{ fontSize: 13, marginBottom: 12 }}>{note}</div>}
        <label className="form-group">
          <div className="form-label">Current password</div>
          <input
            className="form-input"
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            required
            autoComplete="current-password"
          />
        </label>
        <PasswordField label="New password" value={newPassword} onChange={setNewPassword} />
        <label className="form-group">
          <div className="form-label">Confirm new password</div>
          <div style={{ position: 'relative' }}>
            <input
              className="form-input"
              type={showConfirm ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              autoComplete="new-password"
              style={{ width: '100%', boxSizing: 'border-box', paddingRight: 40 }}
            />
            <button
              type="button"
              onClick={() => setShowConfirm(v => !v)}
              aria-label={showConfirm ? 'Hide password' : 'Show password'}
              style={{
                position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                background: 'none', border: 'none', cursor: 'pointer', padding: 4,
                color: 'var(--text-muted)', display: 'flex',
              }}
            >
              {showConfirm ? <EyeOff size={15} /> : <Eye size={15} />}
            </button>
          </div>
        </label>
        <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>
          {saving ? 'Saving…' : 'Change password'}
        </button>
        <div style={{ marginTop: 14, fontSize: 13 }}>
          <Link to="/profile">Update profile</Link>
        </div>
      </form>
    </div>
  );
}
